// This standalone Handlebars specimen has no app/Preact lifecycle to attach to.
export const startRabbitAnimation = (root: HTMLElement) => {
  const frames = root.querySelector<HTMLElement>(".rabbitFrames")!;
  const excerpt = root.querySelector<HTMLElement>(".readingExcerpt")!;
  const original = excerpt.querySelector<HTMLElement>(".original")!;
  const translation = excerpt.querySelector<HTMLElement>(".translation")!;
  const previousLine = excerpt.querySelector<HTMLElement>(
    ".excerptPreviousLine"
  )!;
  const topLine = excerpt.querySelector<HTMLElement>(".excerptTopLine")!;
  const bottomLine = excerpt.querySelector<HTMLElement>(".excerptBottomLine")!;
  const contextBefore = excerpt.querySelector<HTMLElement>(".excerptBefore")!;
  const contextAfter = excerpt.querySelector<HTMLElement>(".excerptAfter")!;
  const motion = root.querySelector<HTMLInputElement>(".motionToggle");
  const override = root.querySelector<HTMLInputElement>(".reducedMotionToggle");
  const preference = window.matchMedia("(prefers-reduced-motion: reduce)");
  const reactions = ["feetTwiddle", "earTwitch"];
  const actions = ["pageTurn", ...reactions];
  const words = [
    [
      "Era una mañana tranquila y el sol iluminaba el sendero. ",
      "En el jardín, una ",
      "mariposa",
      " se posó sobre una flor.",
      "butterfly",
      "Salió de casa y siguió el camino entre los árboles.",
      "Sus alas se abrieron lentamente bajo la luz del sol.",
    ],
    [
      "Al despertarse, abrió la ventana de su cuarto. ",
      "La suave ",
      "brisa",
      " entraba por la ventana.",
      "breeze",
      "Los primeros rayos de sol llenaron la habitación.",
      "Las cortinas se movían y olía a hierba recién cortada.",
    ],
    [
      "Después de tantos años, su amigo la esperaba en la puerta. ",
      "Al verlo, una ",
      "sonrisa",
      " iluminó su rostro.",
      "smile",
      "No sabía qué decir mientras se acercaba a la casa.",
      "Corrió a abrazarlo y los dos se echaron a reír.",
    ],
    [
      "Todo estaba en silencio y la noche estaba tranquila. ",
      "En el cielo, una ",
      "estrella",
      " brillaba sobre el mar.",
      "star",
      "Se sentó en la arena para escuchar las olas.",
      "La miró un rato antes de volver por la playa.",
    ],
  ];
  let wordIndex = 0;
  let nextScene: "discovery" | "pageTurn" = "discovery";
  let lastAction: string | undefined;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const after = (delay: number, callback: () => void) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      timer = undefined;
      callback();
    }, delay);
  };

  const showExcerpt = (visible: boolean) => {
    root.classList.toggle(
      "discoveryExiting",
      !visible &&
        root.classList.contains("discoveryVisible") &&
        root.classList.contains("isPlaying")
    );
    root.classList.toggle("discoveryVisible", visible);
    excerpt.setAttribute("aria-hidden", String(!visible));
  };

  const play = (action: string) => {
    lastAction = action;
    frames.classList.add(action);
  };

  const discover = () => {
    const [preceding, before, spanish, following, english, top, bottom] =
      words[wordIndex];
    wordIndex = (wordIndex + 1) % words.length;
    previousLine.textContent = preceding;
    topLine.textContent = top;
    bottomLine.textContent = bottom;
    original.textContent = spanish;
    translation.textContent = english;
    contextBefore.textContent = before;
    contextAfter.textContent = following;
    translation.setAttribute("aria-hidden", "true");
    root.classList.remove("wordRevealed");
    showExcerpt(true);
    after(1000, () => {
      root.classList.add("wordRevealed");
      translation.removeAttribute("aria-hidden");
      after(450, () => {
        const choices = reactions.filter((action) => action !== lastAction);
        nextScene = "pageTurn";
        play(choices[Math.floor(Math.random() * choices.length)]);
      });
    });
  };

  const rest = () => {
    after(500 + Math.random() * 1500, () => {
      if (nextScene === "pageTurn" && lastAction !== "pageTurn") {
        nextScene = "discovery";
        play("pageTurn");
      } else {
        discover();
      }
    });
  };

  const onActionEnd = (event: AnimationEvent) => {
    if (
      event.target !== frames ||
      !actions.some((action) => frames.classList.contains(action))
    ) {
      return;
    }
    const wasPageTurn = frames.classList.contains("pageTurn");
    frames.classList.remove(...actions);
    if (wasPageTurn) {
      rest();
    } else {
      after(1800, () => {
        showExcerpt(false);
        after(500, () => {
          root.classList.remove("discoveryExiting");
          rest();
        });
      });
    }
  };

  const stop = () => {
    clearTimeout(timer);
    timer = undefined;
    frames.classList.remove(...actions);
    root.classList.remove("isPlaying");
    // A paused/reduced-motion hero still demonstrates the word connection.
    showExcerpt(true);
    root.classList.add("wordRevealed");
    translation.removeAttribute("aria-hidden");
  };

  const syncPlayback = () => {
    stop();
    if (
      document.hidden ||
      !(preference.matches
        ? override?.checked ?? false
        : motion?.checked ?? true)
    ) {
      return;
    }
    showExcerpt(false);
    root.classList.remove("wordRevealed");
    root.classList.add("isPlaying");
    rest();
  };

  frames.addEventListener("animationend", onActionEnd);
  motion?.addEventListener("change", syncPlayback);
  override?.addEventListener("change", syncPlayback);
  preference.addEventListener("change", syncPlayback);
  document.addEventListener("visibilitychange", syncPlayback);
  syncPlayback();

  return () => {
    stop();
    frames.removeEventListener("animationend", onActionEnd);
    motion?.removeEventListener("change", syncPlayback);
    override?.removeEventListener("change", syncPlayback);
    preference.removeEventListener("change", syncPlayback);
    document.removeEventListener("visibilitychange", syncPlayback);
  };
};
